/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

// components imports
import { Modal, Form, Input, Button, message, Row, Col } from "antd";
import { ErrorsComponent } from "../../../UI/ErrorsComponent";

// misc imports
import { AUTH_FORM_LABEL_COL, AUTH_FORM_MAX_WIDTH, AUTH_FORM_WRAPPER_COL } from "@/constants";
import { IRequestError } from "@/interfaces/ServerError.interface";
import { useCreateApiKey } from "@/api/services";

type FieldType = {
  description: string;
};

/**
 * ApiKeyModal component
 * @returns {JSX.Element}
 */
export const ApiKeyModal = ({ serviceKey, isModalOpen, onClose }: { serviceKey: string, isModalOpen: boolean; onClose: () => void }): JSX.Element => {
  const { t } = useTranslation();
  const { workspaceId, projectId } = useParams();
  const params = {
    workspaceId: workspaceId as string,
    projectId: projectId as string,
  };
  const { mutate: createApiKey, isLoading: isCreatingApiKey, error: createApiKeyError } = useCreateApiKey();

  const [messageApi, contextHolder] = message.useMessage();

  const onFinish = (values: Record<string, string>) => {
    const { description } = values;
    createApiKey(
      {
        ...params,
        serviceKey,
        description,
      },
      {
        onSuccess: () => {
          messageApi.open({
            content: t("project_single.api_key.create_success"),
            type: "success",
            duration: 8,
          });
          onClose();
        },
      }
    );
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={t("project_single.api_key.modal_title")}
        open={isModalOpen}
        onCancel={onClose}
        style={{ maxWidth: 600 }}
        width={"95%"}
        centered
        footer={null}
        destroyOnClose
      >
        <Row>
          <Col xs={24}>
            <Form
              name="api-key-form"
              labelCol={{ span: AUTH_FORM_LABEL_COL }}
              labelAlign="left"
              wrapperCol={{ span: AUTH_FORM_WRAPPER_COL }}
              style={{ maxWidth: AUTH_FORM_MAX_WIDTH }}
              onFinish={onFinish}
              autoComplete="off"
            >
              {/* API Key description */}
              <Form.Item<FieldType>
                label={t("project_single.api_key.description")}
                name="description"
                rules={[
                  {
                    required: true,
                    message: t("validation.required"),
                  },
                ]}
              >
                <Input />
              </Form.Item>
              {/* ./ API Key description */}

              {/* Submit button */}
              <Form.Item
                wrapperCol={{
                  offset: AUTH_FORM_LABEL_COL,
                  span: AUTH_FORM_WRAPPER_COL,
                }}
              >
                <Button disabled={isCreatingApiKey} type="primary" htmlType="submit">
                  {t("project_single.api_key.submit")}
                </Button>
                <div className="mt-2">
                  <ErrorsComponent error={createApiKeyError as unknown as IRequestError} />
                </div>
              </Form.Item>
              {/* Submit button */}
            </Form>
          </Col>
        </Row>
      </Modal>
    </>
  );
};
